'use client';
import { useEffect } from 'react';
import Link from 'next/link';
import ContactForm from '@/app/homepage/components/ContactForm';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import Navigation from '@/components/common/Navigation';

export default function ContactPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach(el => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <main className="min-h-screen bg-background">
      <Navigation />
      {/* Page Header */}
      <section className="py-8 md:py-12 border-b border-border bg-card/30 pt-32 md:pt-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-6">
            <Link href="/homepage" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Home
            </Link>
            <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">Contact</span>
          </div>
          <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block reveal">
            Get In Touch
          </span>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6 reveal">
            Ready to Break Ground?
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl reveal" style={{ transitionDelay: '200ms' }}>
            Whether you&apos;re planning a major infrastructure project or need emergency underground utility work,
            our team is ready to deliver. Get a detailed quote within 72 hours.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: CTA Content */}
            <div className="reveal">
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="DocumentTextIcon" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-1">Quote Within 72 Hours</h3>
                    <p className="text-sm text-muted-foreground">
                      Detailed project estimates with equipment specifications, timeline, and transparent pricing.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-muted/30 border border-border rounded-lg mb-8">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong className="text-foreground">Need a direct e-mail address?</strong>
                </p>
                <p className="text-2xl font-bold text-primary mb-1">info@eastcoastug.com</p>
                <p className="text-xs text-muted-foreground">Monday-Friday 7am-7pm EST</p>
              </div>

              <div className="p-6 bg-card border border-border rounded-lg">
                <h3 className="text-lg font-bold text-foreground mb-4">Our Services</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Link href="/services/energy" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                    <Icon name="BoltIcon" size={16} className="text-primary" />
                    Energy Sector
                  </Link>
                  <Link href="/services/telecommunications" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                    <Icon name="SignalIcon" size={16} className="text-primary" />
                    Telecommunications
                  </Link>
                  <Link href="/services/municipal" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                    <Icon name="BuildingOfficeIcon" size={16} className="text-primary" />
                    Municipal Utilities
                  </Link>
                  <Link href="/services/industrial" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                    <Icon name="CogIcon" size={16} className="text-primary" />
                    Industrial Projects
                  </Link>
                </div>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="reveal" style={{ transitionDelay: '200ms' }}>
              <div className="bg-card border border-border rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-foreground mb-2">RFP Inquiry</h2>
                <p className="text-sm text-muted-foreground mb-8">
                  Fill out the form below and our team will contact you within 72 hours with a detailed project estimate.
                </p>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
